/**
 * MovieSaveConstellationModal Component
 * Modal for saving current movie constellation as a preset
 */

import { useState } from 'react';
import { toast } from 'react-hot-toast';
import { useMovieExploreStore } from '../../stores/movie-explore-store';
import { saveMovieConstellation, getSavedMovieConstellations } from '../../services/movie-explore.service';

interface MovieSaveConstellationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function MovieSaveConstellationModal({ isOpen, onClose }: MovieSaveConstellationModalProps) {
  const { nodes, vibe, setSavedConstellations } = useMovieExploreStore();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);

  if (!isOpen) return null;

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      toast.error('Please enter a name for your constellation');
      return;
    }

    if (nodes.length < 3) {
      toast.error('Your constellation must have at least 3 nodes');
      return;
    }

    setSaving(true);

    try {
      await saveMovieConstellation({
        name: name.trim(),
        description: description.trim() || undefined,
        nodes,
        vibe,
      });

      const constellations = await getSavedMovieConstellations();
      setSavedConstellations(constellations);

      toast.success(`Constellation "${name}" saved!`);

      setName('');
      setDescription('');
      onClose();
    } catch (err: any) {
      const errorMessage = err?.response?.data?.error?.message || err?.message || 'Failed to save constellation';
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900">Save Constellation</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSave}>
          <div className="space-y-4 mb-6">
            <div>
              <label htmlFor="movie-constellation-name" className="block text-sm font-medium text-gray-700 mb-1">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                id="movie-constellation-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., French New Wave"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
                maxLength={100}
              />
            </div>

            <div>
              <label htmlFor="movie-constellation-desc" className="block text-sm font-medium text-gray-700 mb-1">
                Description (optional)
              </label>
              <textarea
                id="movie-constellation-desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe this constellation..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                maxLength={500}
              />
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-sm text-blue-800">
              <div className="flex items-start gap-2">
                <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                <div>
                  Saving {nodes.length} nodes with current vibe settings.
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !name.trim()}
              className="px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {saving ? 'Saving...' : 'Save Constellation'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
